import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  DEFAULT_REFRAME_FORM,
  UseAssetReframeReturnValue,
} from "domains/assets/hooks/useAssetReframe";
import { extractFirstQueryParam } from "domains/commons/misc";
import useRouter from "domains/navigation/hooks/useRouter";
import {
  getRatios,
  ratioStringToNumber,
} from "domains/reframe/components/ReframeParams";
import { ReframeForm } from "domains/reframe/interfaces/Reframe";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import {
  GetAssetsByAssetIdApiResponse,
  useLazyGetAssetsByAssetIdQuery,
} from "infra/api/generated/api";
import _ from "lodash";

interface UseImportParamsReframeArg {
  setForm: UseAssetReframeReturnValue["setForm"];
  setValue: UseAssetReframeReturnValue["setValue"];
}

export default function useImportParamsReframe({
  setForm,
  setValue,
}: UseImportParamsReframeArg) {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();

  const urlParamsRef = useRef<{
    importAssetId?: string;
    restartExpand?: string;
  }>({});
  const urlParams = useMemo(
    () =>
      router.isReady
        ? {
            importAssetId: extractFirstQueryParam(router.query.importAssetId),
            restartExpand: extractFirstQueryParam(router.query.restartExpand),
          }
        : {},
    [router.isReady, router.query.importAssetId, router.query.restartExpand]
  );

  // ----------------------------------

  const importAssetFromId = useCallback(
    async (assetId: string) => {
      const { data: assetData } = await getAssetById({
        projectId: selectedProject.id,
        assetId,
      });

      if (!assetData?.asset) {
        return;
      }

      const importedAsset = assetData.asset;
      setValue("asset", importedAsset);
    },
    [getAssetById, selectedProject.id, setValue]
  );

  const restartExpand = useCallback(
    async (assetId: string) => {
      const assetToOriginalForm = async ({
        reframeAsset,
        originalImageAsset,
      }: {
        reframeAsset: GetAssetsByAssetIdApiResponse["asset"];
        originalImageAsset: GetAssetsByAssetIdApiResponse["asset"];
      }): Promise<Partial<ReframeForm>> => {
        if (
          !reframeAsset?.metadata?.image ||
          !reframeAsset?.metadata?.targetWidth ||
          !reframeAsset?.metadata?.targetHeight ||
          reframeAsset?.metadata?.overlapPercentage === undefined ||
          !originalImageAsset
        ) {
          return {};
        }

        const aspectRatios = getRatios({
          width: reframeAsset.metadata.width,
          height: reframeAsset.metadata.height,
        });

        const aspectRatio = aspectRatios.find(
          (ratio) =>
            ratioStringToNumber(ratio, {
              width: reframeAsset.metadata.width,
              height: reframeAsset.metadata.height,
            }) ===
            (reframeAsset.metadata.targetWidth ?? 0) /
              (reframeAsset.metadata.targetHeight ?? 0)
        );

        return {
          overlapPercentage: reframeAsset.metadata.overlapPercentage * 2 * 100,
          asset: originalImageAsset,
          targetWidth: reframeAsset.metadata.targetWidth,
          targetHeight: reframeAsset.metadata.targetHeight,
          inputScale: reframeAsset.metadata.resizeOption ?? 1,
          inputWidth: originalImageAsset.metadata.width
            ? originalImageAsset.metadata.width *
              (reframeAsset.metadata.resizeOption ?? 1)
            : undefined,
          inputHeight: originalImageAsset.metadata.height
            ? originalImageAsset.metadata.height *
              (reframeAsset.metadata.resizeOption ?? 1)
            : undefined,
          aspectRatio,
          inputLocation: reframeAsset.metadata.inputLocation,
          prompt: reframeAsset.metadata.prompt ?? DEFAULT_REFRAME_FORM.prompt,
          numInferenceSteps:
            reframeAsset.metadata.numInferenceSteps ??
            DEFAULT_REFRAME_FORM.numInferenceSteps,
        };
      };

      const { data: assetData } = await getAssetById({
        projectId: selectedProject.id,
        assetId,
      });
      if (!assetData?.asset) {
        return;
      }

      const importedAsset = assetData.asset;
      if (!importedAsset.metadata.image) {
        return;
      }
      const { data: originalImageAssetData } = await getAssetById({
        projectId: selectedProject.id,
        assetId: importedAsset.metadata.image,
      });
      const originalImageAsset = originalImageAssetData?.asset;
      if (!originalImageAsset) {
        return;
      }

      const formUpdate = await assetToOriginalForm({
        reframeAsset: importedAsset,
        originalImageAsset,
      });

      setForm((prevForm) => ({
        ...prevForm,
        ...formUpdate,
      }));
    },
    [getAssetById, selectedProject.id, setForm]
  );

  // ----------------------------------

  useEffect(() => {
    if (_.isEqual(urlParams, urlParamsRef.current)) {
      return;
    }

    urlParamsRef.current = urlParams;

    if (urlParams.importAssetId) {
      void router.replace({
        pathname: router.pathname,
        query: _.omit(router.query, "importAssetId", "restartExpand"),
      });
    }

    if (urlParams.importAssetId) {
      void importAssetFromId(urlParams.importAssetId);
    }
    if (urlParams.restartExpand) {
      void restartExpand(urlParams.restartExpand);
    }
  }, [importAssetFromId, restartExpand, router, urlParams]);
}
